/**
 * Day 3, competitor discovery and confirmation, from that stage onward:
 * the discovery panel, the candidate cards, the confirmation panel and the
 * set history, in the order the Owner works through them.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import type {
  AnalysisDay2State,
  AnalysisDay3State,
  AnalysisRunSummary,
  AnalysisStageAttempt,
} from '../../api.js';
import { SectionHeading } from '../common.js';
import { CompetitorCandidateCards } from './CompetitorCandidateCards.js';
import { CompetitorConfirmationPanel } from './CompetitorConfirmationPanel.js';
import { CompetitorDiscoveryPanel } from './CompetitorDiscoveryPanel.js';
import { CompetitorSetHistory } from './CompetitorSetHistory.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { CompetitorForms } from './useCompetitorForms.js';

export function CompetitorSection({
  id,
  run,
  attempts,
  day2,
  day3,
  busy,
  mutate,
  forms,
  copy,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly attempts: readonly AnalysisStageAttempt[];
  readonly day2: AnalysisDay2State;
  readonly day3: AnalysisDay3State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly forms: CompetitorForms;
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('COMPETITOR_DISCOVERY')
  ) {
    return null;
  }

  return (
    <section id="competitors" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>{copy.competitors}</SectionHeading>

      <CompetitorDiscoveryPanel
        id={id}
        run={run}
        attempts={attempts}
        day2={day2}
        day3={day3}
        busy={busy}
        mutate={mutate}
        forms={forms}
      />

      <CompetitorCandidateCards
        id={id}
        run={run}
        day3={day3}
        busy={busy}
        mutate={mutate}
        competitorReason={forms.competitorReason}
      />

      <CompetitorConfirmationPanel
        id={id}
        run={run}
        day3={day3}
        busy={busy}
        mutate={mutate}
        forms={forms}
      />

      <CompetitorSetHistory
        id={id}
        run={run}
        day3={day3}
        busy={busy}
        mutate={mutate}
        forms={forms}
      />
    </section>
  );
}
